/**
 * Configuration YAML Viewer Component
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import {
  Code as CodeIcon,
  ContentCopy as ContentCopyIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

import { DeviceConfigYAML } from '../../types/config';
import { configApi } from '../../services/configApi';

interface ConfigYamlViewerProps {
  deviceId: number;
}

const ConfigYamlViewer: React.FC<ConfigYamlViewerProps> = ({ deviceId }) => {
  const [yamlConfig, setYamlConfig] = useState<DeviceConfigYAML | null>(null);
  const [yamlString, setYamlString] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [showCopySuccess, setShowCopySuccess] = useState(false);

  useEffect(() => {
    loadYamlConfig();
  }, [deviceId]);

  const loadYamlConfig = async () => {
    try {
      setLoading(true);
      setError('');
      const config = await configApi.getConfigYaml(deviceId);
      setYamlConfig(config);
      setYamlString(convertToYamlString(config));
    } catch (error: any) {
      console.error('Failed to load YAML configuration:', error);
      setError(error.response?.data?.detail || 'Failed to load YAML configuration');
    } finally {
      setLoading(false);
    }
  };

  const convertToYamlString = (config: DeviceConfigYAML): string => {
    return `# MODO Device Configuration
# Generated on: ${new Date().toISOString()}

# OCXO Control Configuration
OCXO_CONTROL_TYPE: "${config.OCXO_CONTROL_TYPE}"
PPS_OFFSET_NS: ${config.PPS_OFFSET_NS}
OCXO_CONTROL_BASELINE: ${config.OCXO_CONTROL_BASELINE}

# Steering Configuration  
STEERING_MODE: "${config.STEERING_MODE}"

# Device Configuration
# Note: IS_ADSB_ENABLED is automatically set based on STEERING_MODE
IS_ADSB_ENABLED: ${config.IS_ADSB_ENABLED}
NUM_SLOTS: ${config.NUM_SLOTS}
LEVEL: ${config.LEVEL}
LATITUDE: ${config.LATITUDE}
LONGITUDE: ${config.LONGITUDE}
HEIGHT_M: ${config.HEIGHT_M}

# Open Loop Configuration
OPENLOOP_COMMAND: ${config.OPENLOOP_COMMAND}

# GNSS Configuration
GNSS_INPUTS:
${config.GNSS_INPUTS.map(input => `  - "${input}"`).join('\n')}

GNSS_INTERNAL_DELAYS:
${config.GNSS_INTERNAL_DELAYS.map(delay => `  - ${delay}`).join('\n')}

GNSS_CABLE_DELAYS:
${config.GNSS_CABLE_DELAYS.map(delay => `  - ${delay}`).join('\n')}

# ADS-B Configuration
ADSB_INTERNAL_DELAY: ${config.ADSB_INTERNAL_DELAY}
ADSB_CABLE_DELAY: ${config.ADSB_CABLE_DELAY}

# External Reference Configuration
EXT_REF_INTERNAL_DELAY: ${config.EXT_REF_INTERNAL_DELAY}
EXT_REF_CABLE_DELAY: ${config.EXT_REF_CABLE_DELAY}

# Controller Configuration
CONTROLLER_CONFIG:
  type: "${config.CONTROLLER_CONFIG.type}"
${config.CONTROLLER_CONFIG.type === 'pi' ? `  pi_gains:
    Kp: ${config.CONTROLLER_CONFIG.pi_gains.Kp}
    Ki: ${config.CONTROLLER_CONFIG.pi_gains.Ki}` : ''}${config.CONTROLLER_CONFIG.type === 'kalman' ? `  kalman_config:
    input_window_size: ${config.CONTROLLER_CONFIG.kalman_config.input_window_size}` : ''}
`;
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(yamlString);
      setShowCopySuccess(true);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([yamlString], { type: 'text/yaml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `device_${deviceId}_config.yaml`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" action={
        <Button color="inherit" size="small" onClick={loadYamlConfig}>
          Retry
        </Button>
      }>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Paper 
        elevation={0} 
        sx={{ 
          borderRadius: 3, 
          border: '1px solid rgba(255, 255, 255, 0.08)',
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <Box 
          sx={{ 
            p: 3, 
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
            bgcolor: 'rgba(0, 0, 0, 0.02)'
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center">
              <CodeIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" fontWeight={600}>
                YAML Configuration
              </Typography>
            </Box>
            
            <Box display="flex" gap={1}>
              <Tooltip title="Refresh YAML">
                <IconButton onClick={loadYamlConfig} size="small">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Copy to Clipboard">
                <IconButton onClick={handleCopyToClipboard} size="small">
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Download YAML File">
                <IconButton onClick={handleDownload} size="small">
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {/* YAML Content */}
        <Box 
          sx={{ 
            p: 0,
            bgcolor: '#1e1e1e',
            color: '#d4d4d4',
            fontFamily: '"Fira Code", "Monaco", "Menlo", "Ubuntu Mono", monospace',
            fontSize: '14px',
            lineHeight: 1.5,
            maxHeight: '600px',
            overflow: 'auto'
          }}
        >
          <pre 
            style={{ 
              margin: 0, 
              padding: '24px',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}
          >
            <code>{yamlString}</code>
          </pre>
        </Box>

        {/* Footer Info */}
        <Box 
          sx={{ 
            p: 2, 
            bgcolor: 'rgba(0, 0, 0, 0.02)',
            borderTop: '1px solid rgba(255, 255, 255, 0.08)'
          }}
        >
          <Typography variant="body2" color="text.secondary">
            This YAML configuration will be deployed to the device config.yaml file.
            Make sure to save any changes before deploying.
          </Typography>
        </Box>
      </Paper>

      {/* Copy Success Snackbar */}
      <Snackbar
        open={showCopySuccess}
        autoHideDuration={3000}
        onClose={() => setShowCopySuccess(false)}
        message="Configuration copied to clipboard"
      />
    </Box>
  );
};

export default ConfigYamlViewer; 